// src/pages/PatientUploadPage.jsx
// Route: /patient/upload
//
// Patient-facing lab report upload screen.
// Patients upload a blood test PDF → auto-triage pipeline fires →
// on completion redirect to /patient/results/:triageId.
//
// Architecture:
//   - Reuses useAutoTriage() hook (same pipeline as navigator HealthRecordsTab)
//   - Reuses healthRecordAPI from src/services/api.js (no direct fetch calls)
//   - Does NOT reuse navigator HealthRecordsTab (different UX: simpler, no delete/edit)
//   - PatientAppShell wraps the page
//
// Gap Report — PatientUploadPage.jsx
//   ✅ Confirmed: upload → auto-triage → redirect to results
//   ⚠️ No Figma frame provided → follows PatientSettingsPage / PatientLoginPage design language
//   ❌ Missing Figma frames: all states (idle, uploading, triaging, done, error)
//   🔗 Reused: PatientAppShell, usePatient(), useAutoTriage(), healthRecordAPI
//   🚫 Deviations: None — all CSS vars, no hardcoded hex

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { healthRecordAPI } from '../services/api';
import { useAutoTriage, TRIAGE_STAGES, isScanImage } from '../components/healthrecords/useAutoTriage';
import PhotoQualityNotice, { usePhotoQuality } from '../components/healthrecords/PhotoQualityNotice';
import PhotoCapture from '../components/healthrecords/PhotoCapture';
import IdentityMismatchModal from '../components/healthrecords/IdentityMismatchModal';
import { RECORD_TYPES } from '../constants/recordTypes';
import Icon from '../components/Icon';
import { extractPdfText } from '../utils/pdfText';
import { extractReportDate, formatSuggestion, toLocalIsoDate } from '../utils/reportDate';

const MAX_FILE_MB = 20;

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Triage progress step labels (patient-friendly wording) ───────────────────
const PATIENT_STAGE_LABELS = {
  IDLE:        '',
  EXTRACTING:  'Reading your report…',
  SAVING_TEXT: 'Securing your data…',
  CLAUDE:      'AI is analysing your results…',
  RF:          'Cross-checking with clinical model…',
  SAVING:      'Saving your triage report…',
  DONE:        'Done! Preparing your results…',
  HELD:        'Your report is being checked by your navigator…',
  FAILED:      'Analysis failed',
  SKIPPED:     'Not a blood test — no triage available',
  AWAITING_IDENTITY_CONFIRMATION: 'Please confirm this report is yours…',
  CANCELLED:   'Upload cancelled',
};

// ── Upload form ───────────────────────────────────────────────────────────────
export default function PatientUploadPage() {
  const { patient, profile } = usePatient();
  const navigate       = useNavigate();
  const fileInputRef   = useRef(null);
  const statusRef      = useRef(null);

  const [file,       setFile]       = useState(null);
  const [recordType, setRecordType] = useState('BLOOD_TEST');
  const [drawDate,   setDrawDate]   = useState('');
  const [labName,    setLabName]    = useState('');
  const [dragOver,   setDragOver]   = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [error,      setError]      = useState('');
  const photoProblems = usePhotoQuality(file, recordType);

  // "Date of test" suggested from the picked report (utils/reportDate.js). Only ever a suggestion:
  // once the patient edits the date it is theirs, a result from an older pick is ignored, and a
  // suggestion only replaces an empty field or the previous suggestion.
  const [dateHint, setDateHint] = useState(null); // { date, source } shown under the field
  const dateEditedRef  = useRef(false);
  const pickRef        = useRef(0);
  const suggestionRef  = useRef(null);
  const drawDateRef    = useRef(drawDate);
  drawDateRef.current  = drawDate;
  const [capturing, setCapturing] = useState(false);

  // useAutoTriage declared before useEffect that references its state values
  const {
    stage,
    triageError,
    isTriaging,
    identityMismatch,
    runAutoTriage,
    confirmIdentityMismatch,
    cancelIdentityMismatch,
    reset: resetTriage,
  } = useAutoTriage();

  // Scroll status banner into view when triage starts or fails
  useEffect(() => {
    if ((isTriaging || stage === 'DONE' || stage === 'FAILED') && statusRef.current) {
      statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isTriaging, stage]);

  const isBloodTestPdf = recordType === 'BLOOD_TEST' && (file?.type === 'application/pdf' || isScanImage(file?.type));
  const showTriageProgress = isTriaging || stage === 'DONE' || stage === 'FAILED';
  const stageLabel = PATIENT_STAGE_LABELS[stage] || '';

  function handleFileSelect(f) {
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File too large — maximum ${MAX_FILE_MB}MB`);
      return;
    }
    setFile(f);
    setError('');
    pickRef.current += 1;
    suggestTestDate(f, pickRef.current); // not awaited: never holds up the pick
  }

  // Never throws: no date, a scanned PDF, an unreadable file or any error is just "no suggestion".
  async function suggestTestDate(f, pick) {
    let found = null;
    try {
      const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');
      const text = isPdf ? await extractPdfText(f, { maxPages: 2 }) : '';
      found = extractReportDate(text, f.name, { dob: profile?.dateOfBirth || patient?.dateOfBirth });
    } catch {
      found = null;
    }
    if (pick !== pickRef.current || dateEditedRef.current) return;
    const previous = suggestionRef.current;
    const current = drawDateRef.current;
    if (current !== '' && current !== previous) return;
    if (found) {
      suggestionRef.current = found.date;
      setDrawDate(found.date);
      setDateHint(found);
    } else if (previous) {
      suggestionRef.current = null;
      setDrawDate('');
      setDateHint(null);
    }
  }

  async function handleUpload() {
    if (!file)     { setError('Please select a file first.'); return; }
    if (!drawDate) { setError('Please enter the date of your blood test.'); return; }
    if (!patient?.id) { setError('Session error — please log in again.'); return; }

    setError('');
    setUploading(true);
    resetTriage();

    let newRecord;
    try {
      const res = await healthRecordAPI.upload(patient.id, file, {
        recordType,
        drawDate,
        labName: labName || undefined,
      });
      newRecord = res.data;
    } catch (e) {
      setError(e.response?.data?.message || 'Upload failed — please try again.');
      setUploading(false);
      return;
    }
    setUploading(false);

    // Auto-triage only runs on blood test PDFs
    if (isBloodTestPdf) {
      const outcome = await runAutoTriage(newRecord, file, patient, patient.id);
      handleTriagePipelineOutcome(outcome);
    } else {
      // Non-blood-test or non-PDF — just go to timeline
      navigate('/patient/timeline');
    }
  }

  /**
   * Shared outcome handling for both the initial upload and the resumed
   * pipeline after an identity-mismatch confirmation. Reads the {status}
   * useAutoTriage just returned, NOT the hook's `stage` state — that state
   * update is scheduled by React, not guaranteed visible in this same
   * synchronous continuation, so the returned status is the only value this
   * function can trust right after an await.
   */
  function handleTriagePipelineOutcome({ status, triageResult, error: outcomeError } = {}) {
    if (triageResult?.id) {
      navigate(`/patient/results/${triageResult.id}`);
      return;
    }
    // DEF-023 follow-up: paused for confirmation — stay put, the modal
    // renders itself off `identityMismatch` below. Don't treat this as
    // "done, go to timeline" or "failed."
    if (status === 'AWAITING_IDENTITY_CONFIRMATION' || status === 'CANCELLED') {
      return;
    }
    if (status === 'FAILED') {
      setError(outcomeError || 'AI analysis failed. Your report was saved — your navigator will review it shortly.');
      return;
    }
    // HELD (a navigator is checking it; the timeline lists it under "Reports being checked"), DONE with no triage result, or SKIPPED — go to timeline
    navigate('/patient/timeline');
  }

  async function handleConfirmIdentity(comment) {
    const outcome = await confirmIdentityMismatch(comment);
    handleTriagePipelineOutcome(outcome);
  }

  function handleCancelIdentity() {
    cancelIdentityMismatch();
    setError('Upload cancelled — the report was not saved.');
  }

  // ── Step indicator ───────────────────────────────────────────────────────
  const steps = [
    {
      label:  'Upload',
      done:   uploading || isTriaging || stage === 'DONE',
      active: !!file && !uploading && !isTriaging && stage === 'IDLE',
    },
    {
      label:  'Analyse',
      done:   stage === 'DONE',
      active: isTriaging || uploading,
    },
    {
      label:  'Results',
      done:   false,
      active: stage === 'DONE',
    },
  ];

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Upload Report' }]}>
      <div style={{
        maxWidth: 560, margin: '0 auto',
        padding: '24px 0 80px',
        fontFamily: 'var(--font-body)',
      }}>

        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.1em', color: 'var(--brand-accent)', marginBottom: 4,
          }}>
            Health Records
          </div>
          <div style={{
            fontFamily: 'var(--font-display)', fontSize: 24,
            color: 'var(--text-primary)', marginBottom: 8,
          }}>
            Upload a report
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Upload a blood test PDF and we'll analyse it within minutes — your results and
            risk score will appear automatically.
          </div>
        </div>

        {/* Step indicator */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 0,
          marginBottom: 28,
        }}>
          {steps.map((s, i) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', flex: i < steps.length - 1 ? 1 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                  background: s.done ? 'var(--green)' : s.active ? 'var(--brand-dark)' : 'var(--bg-hover)',
                  color: s.done ? 'var(--text-inverse)' : s.active ? 'var(--text-on-brand-dark)' : 'var(--text-muted)',
                }}>
                  {s.done ? '✓' : s.active && !s.done ? '●' : i + 1}
                </div>
                <span style={{
                  fontSize: 12, fontWeight: 600,
                  color: s.done ? 'var(--green)' : s.active ? 'var(--brand-dark)' : 'var(--text-muted)',
                }}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div style={{
                  flex: 1, height: 2, margin: '0 8px',
                  background: s.done ? 'var(--green)' : 'var(--border)',
                  borderRadius: 1,
                }} />
              )}
            </div>
          ))}
        </div>

        {/* Status banner — shown above card when triaging, succeeding or failing */}
        {showTriageProgress && (
          <div ref={statusRef} style={{
            marginBottom: 20, padding: '16px 20px',
            background: stage === 'DONE' ? 'var(--green-bg)'
              : stage === 'FAILED' ? 'var(--red-bg)'
              : 'var(--bg-subtle)',
            border: `1px solid ${stage === 'DONE' ? 'var(--green)'
              : stage === 'FAILED' ? 'var(--red)' : 'var(--amber)'}`,
            borderRadius: 'var(--radius-lg)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isTriaging ? 12 : 0 }}>
              <span style={{ fontSize: 20 }}>
                {stage === 'DONE' ? '✅' : stage === 'FAILED' ? '⚠️' : '⏳'}
              </span>
              <div>
                <div style={{
                  fontSize: 14, fontWeight: 600,
                  color: stage === 'DONE' ? 'var(--green)'
                    : stage === 'FAILED' ? 'var(--red)' : 'var(--text-primary)',
                }}>
                  {stage === 'DONE' ? 'Analysis complete — redirecting to your results…'
                    : stage === 'FAILED' ? 'Analysis could not be completed'
                    : stageLabel}
                </div>
                {stage === 'FAILED' && (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                    {triageError || 'Your report was saved securely. Your navigator will review it and be in touch.'}
                  </div>
                )}
                {stage === 'FAILED' && (
                  <button
                    type="button"
                    onClick={() => navigate('/patient/timeline')}
                    style={{
                      marginTop: 10, padding: '8px 16px',
                      border: 'none', borderRadius: 'var(--radius-md)',
                      background: 'var(--brand-dark)', color: 'var(--text-on-brand-dark)',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                    }}
                  >
                    View my health story →
                  </button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {isTriaging && (
              <div style={{ height: 4, background: 'var(--bg-hover)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  width: `${((TRIAGE_STAGES[stage]?.step ?? 0) / 6) * 100}%`,
                  background: 'var(--brand-accent)',
                  transition: 'width 0.4s ease',
                }} />
              </div>
            )}
          </div>
        )}

        {/* Upload card */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
        }}>

          {/* Drop zone */}
          <div
            role="button"
            tabIndex={0}
            aria-label="Upload file — click or drag and drop"
            style={{
              border: `2px dashed ${dragOver ? 'var(--brand-accent)' : file ? 'var(--green)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-md)',
              padding: '32px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragOver ? 'var(--bg-subtle)' : file ? 'var(--green-bg)' : 'var(--bg-subtle)',
              transition: 'all 0.15s',
              marginBottom: 20,
              outline: 'none',
            }}
            onClick={() => !uploading && !isTriaging && fileInputRef.current?.click()}
            onKeyDown={e => e.key === 'Enter' && !uploading && !isTriaging && fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              handleFileSelect(e.dataTransfer.files[0]);
            }}
          >
            {file ? (
              <>
                <div style={{ fontSize: 32, marginBottom: 8 }}>
                  {recordType === 'BLOOD_TEST' ? '🩸' : '📄'}
                </div>
                <div style={{
                  fontSize: 14, fontWeight: 600,
                  color: 'var(--green)', marginBottom: 4,
                }}>
                  {file.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {formatSize(file.size)} · {file.type === 'application/pdf' ? 'PDF' : file.type}
                </div>
                {!uploading && !isTriaging && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    Click to change file
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="empty-icon empty-icon--badge" aria-hidden="true">
              <Icon name="paperclip" size={24} />
            </div>
                <div style={{
                  fontSize: 15, fontWeight: 600,
                  color: 'var(--text-primary)', marginBottom: 6,
                }}>
                  Click to select your report
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  or drag and drop here
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  PDF, JPG, PNG — up to {MAX_FILE_MB}MB
                </div>
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.bmp,.tif,.tiff"
            style={{ display: 'none' }}
            onChange={e => handleFileSelect(e.target.files[0])}
          />

          <div style={{ marginBottom: 10 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCapturing(true)}>Take a photo of a paper report</button>
          </div>
          {capturing && <PhotoCapture onClose={() => setCapturing(false)} onCapture={(f) => { setCapturing(false); handleFileSelect(f); }} />}
          <PhotoQualityNotice problems={photoProblems} file={file} recordType={recordType} />

          {/* Record type grid */}
          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600,
              color: 'var(--text-secondary)', marginBottom: 10,
            }}>
              Type of report
            </label>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10,
            }}>
              {RECORD_TYPES.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setRecordType(t.value)}
                  style={{
                    padding: '14px 10px',
                    border: `1.5px solid ${recordType === t.value ? 'var(--teal)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-md)',
                    background: recordType === t.value ? 'var(--teal-bg)' : 'var(--bg-subtle)',
                    color: recordType === t.value ? 'var(--text-primary)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    fontSize: 13, fontWeight: recordType === t.value ? 700 : 500,
                    transition: 'all 0.15s',
                    fontFamily: 'var(--font-body)',
                  }}
                >
                  <span style={{ fontSize: 22 }}>{t.icon}</span>
                  <span>{t.label}</span>
                </button>
              ))}
            </div>
            {isBloodTestPdf && (
              <div style={{
                marginTop: 10, padding: '8px 12px',
                background: 'var(--green-bg)',
                border: '1px solid var(--green)',
                borderRadius: 'var(--radius-md)',
                fontSize: 12, color: 'var(--green)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>✓</span>
                <span>
                  Blood test PDF detected — we'll automatically analyse your results and show your risk score.
                </span>
              </div>
            )}
          </div>

          {/* Date + lab */}
          <div className="form-grid" style={{ gap: 14, marginBottom: 20 }}>
            <div>
              <label htmlFor="drawDate" style={{
                display: 'block', fontSize: 13, fontWeight: 600,
                color: 'var(--text-secondary)', marginBottom: 6,
              }}>
                Date of test <span style={{ color: 'var(--red)' }}>*</span>
              </label>
              <input
                id="drawDate"
                type="date"
                value={drawDate}
                onChange={e => {
                  dateEditedRef.current = true;
                  setDateHint(null);
                  setDrawDate(e.target.value);
                }}
                max={toLocalIsoDate(new Date())}
                aria-describedby={dateHint ? 'drawDateHint' : undefined}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1.5px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '11px 12px', fontSize: 14,
                  fontFamily: 'var(--font-body)',
                  color: 'var(--text-primary)',
                  background: 'var(--bg-subtle)', outline: 'none',
                }}
                onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
              />
              {dateHint && (
                <div id="drawDateHint" className="form-hint upload-date-hint">
                  {`${dateHint.source === 'filename' ? 'Taken from the file name' : 'Found in your report'}: ${formatSuggestion(dateHint.date)}. Change it if it's wrong.`}
                </div>
              )}
            </div>
            <div>
              <label htmlFor="labName" style={{
                display: 'block', fontSize: 13, fontWeight: 600,
                color: 'var(--text-secondary)', marginBottom: 6,
              }}>
                Lab / hospital (optional)
              </label>
              <input
                id="labName"
                type="text"
                value={labName}
                onChange={e => setLabName(e.target.value)}
                placeholder="e.g. Thyrocare, Metropolis"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1.5px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '11px 12px', fontSize: 14,
                  fontFamily: 'var(--font-body)',
                  color: 'var(--text-primary)',
                  background: 'var(--bg-subtle)', outline: 'none',
                }}
                onFocus={e => (e.target.style.borderColor = 'var(--brand-accent)')}
                onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
              />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              marginBottom: 16, padding: '10px 14px',
              background: 'var(--red-bg)',
              border: '1px solid var(--red)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13, color: 'var(--red)',
            }} role="alert">
              ⚠️ {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="button"
            onClick={handleUpload}
            disabled={uploading || isTriaging || !file}
            style={{
              width: '100%', border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '14px 20px', fontSize: 15, fontWeight: 600,
              color: uploading || isTriaging || !file ? 'var(--text-inverse)' : 'var(--brand-dark)',
              background: uploading || isTriaging || !file
                ? 'var(--text-muted)'
                : 'var(--teal)',
              boxShadow: uploading || isTriaging || !file ? 'none' : '0 0 20px var(--teal-light)',
              cursor: uploading || isTriaging || !file ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)',
              transition: 'background 0.15s',
            }}
          >
            {uploading ? '⏳ Uploading…'
              : isTriaging ? '🔬 Analysing your report…'
              : '🤖 Upload and analyse →'}
          </button>

          {/* Info note */}
          <div style={{
            marginTop: 14, fontSize: 12, color: 'var(--text-muted)',
            lineHeight: 1.5, textAlign: 'center',
          }}>
            {isBloodTestPdf
              ? 'Your blood test PDF will be read by AI and your navigator will be notified of the results.'
              : 'Your report will be securely stored. Your navigator will be notified.'}
          </div>
        </div>

        {/* Help text below card */}
        <div style={{
          marginTop: 20, padding: '16px 20px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600,
            color: 'var(--text-primary)', marginBottom: 8,
          }}>
            What happens next?
          </div>
          {[
            { icon: '🔒', text: 'Your report is securely uploaded and encrypted.' },
            { icon: '🤖', text: 'Our AI reads the values and computes your risk score (blood tests only).' },
            { icon: '📊', text: 'Your results appear instantly — you\'ll be taken there automatically.' },
            { icon: '💬', text: 'Your navigator is notified and will follow up with you.' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              marginBottom: i < 3 ? 8 : 0,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {item.text}
              </span>
            </div>
          ))}
        </div>

      </div>

      {stage === 'AWAITING_IDENTITY_CONFIRMATION' && (
        <IdentityMismatchModal
          mismatch={identityMismatch}
          onConfirm={handleConfirmIdentity}
          onCancel={handleCancelIdentity}
        />
      )}
    </PatientAppShell>
  );
}
